import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Spinner, Tab } from 'react-bootstrap';
import { usePagedArray, Pager } from 'rentalui-finance';
import DashboardTabs from '../components/DashboardTabs';
import { getItem, getMyBookings, imageUrl, sendReminder } from '../api';
import ProductDetailsModal from '../components/ProductDetailsModal';
import OrderDetailsModal from '../components/OrderDetailsModal';

// Story 15 - a deposit refund that's still REFUND_PENDING (return confirmed, or the booking was
// cancelled, but the money hasn't actually reached the customer yet) keeps the order out of
// Completed - see bucketOf() below.
function needsRefund(booking) {
  return booking.depositAmount > 0 && booking.depositStatus === 'REFUND_PENDING';
}

// A cancelled booking nobody still has to act on: no deposit refund outstanding and no
// owner-recorded payment still awaiting finance reconciliation. These drop out of the
// Upcoming/Active/Completed lists into their own "Cancelled" tab; a cancelled booking that still
// needs its refund sent stays in Active (via needsRefund below) so it can't be lost track of.
function isResolvedCancellation(booking) {
  return booking.status === 'cancelled' && !needsRefund(booking) && !booking.payment?.needsReconciliation;
}

// Data-driven "Action Required" banner (MyBookings user story 1.3) - derived from booking state,
// not stored/hardcoded per booking. needsRefund is checked before the cancelled-booking early
// return below since a pending refund is exactly as relevant (often more so) on a cancelled
// booking as on a completed one.
function actionBanner(booking) {
  if (needsRefund(booking)) return '⏳ Deposit refund pending';
  if (booking.status === 'cancelled') return null;
  if (booking.depositStatus === 'REFUNDED') return '🔔 Security deposit refunded';
  if (booking.returnStatus === 'RETURN_SCHEDULED') return '🔔 Return scheduled - check the details';
  if (booking.deliveryStatus === 'DELIVERED' && booking.returnStatus === 'NOT_DUE') {
    const daysLeft = Math.ceil((new Date(booking.endDate) - Date.now()) / 86400000);
    if (daysLeft <= 1 && daysLeft >= 0) return '🔔 Return due tomorrow';
  }
  if (booking.status === 'confirmed' && booking.deliveryStatus === 'PENDING') return '🔔 Owner accepted your booking';
  return null;
}

// Upcoming / Active / Completed bucketing (MyBookings user story 1.2).
function bucketOf(booking) {
  // A settled cancellation is done with - it belongs in its own tab, not mixed into the
  // active/completed lifecycle where it reads as noise.
  if (isResolvedCancellation(booking)) return 'cancelled';
  const now = Date.now();
  const start = new Date(booking.startDate).getTime();
  const end = new Date(booking.endDate).getTime();
  // Owner Bookings & Cancellation v2 doc, Story 13c - the owner/admin side treats
  // `returnStatus: 'COMPLETED'` as the sole source of truth for "this order is done", shown in
  // OwnerDashboard.js's "Completed Orders" tab. This bucketing used to rely on `end < now` alone,
  // which disagreed with that whenever a return was confirmed before the booking's own end date
  // (e.g. an early return) - the order sat in the owner's Completed tab while still showing here
  // as Active/Upcoming, undercounting this tab vs the owner's. Checking returnStatus first keeps
  // the two in sync; the date fallback below still covers bookings nobody has explicitly closed
  // out (no delivery/return flow, or the owner simply hasn't confirmed the return yet).
  const isDone = booking.status === 'cancelled' || booking.returnStatus === 'COMPLETED' || end < now;
  // Story 15: don't count it as Completed while its deposit refund is still pending, even if
  // it's otherwise "done" (returned/cancelled/past its end date) - matches OwnerDashboard.js's
  // completed/active split, so the two "Completed" counts can't drift apart over a refund that
  // hasn't actually gone out yet.
  if (isDone && needsRefund(booking)) return 'active';
  if (isDone) return 'completed';
  if (booking.status === 'confirmed' && start <= now && now <= end) return 'active';
  return 'upcoming';
}

const STATUS_VARIANT = { confirmed: 'success', cancelled: 'danger', pending: 'warning' };

// Owner Bookings & Cancellation v2 doc, Story 13a - a booking is only ever in one of these two
// reminder-eligible states at a time (pending, before it's even confirmed; or confirmed with the
// return scheduled/picked-up), so "Remind owner"/"Remind about return" below are mutually
// exclusive per booking.
const RETURN_DUE_STATUSES = ['RETURN_SCHEDULED', 'PICKED_UP'];

export default function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [itemsById, setItemsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reminded, setReminded] = useState({});
  const [tab, setTab] = useState('upcoming');
  const [productItemId, setProductItemId] = useState(null); // Story 14
  const [orderDetailsBooking, setOrderDetailsBooking] = useState(null); // fix_issues3.md - order popup

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const myBookings = await getMyBookings();
      setBookings(myBookings);
      const uniqueItemIds = [...new Set(myBookings.map((b) => b.itemId))];
      const items = await Promise.all(uniqueItemIds.map((id) => getItem(id).catch(() => null)));
      setItemsById(Object.fromEntries(uniqueItemIds.map((id, i) => [id, items[i]])));
    } catch {
      setError('Could not load your bookings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRemind = async (id) => {
    setReminded((prev) => ({ ...prev, [id]: 'sending' }));
    try {
      await sendReminder(id);
      setReminded((prev) => ({ ...prev, [id]: 'sent' }));
    } catch {
      setReminded((prev) => ({ ...prev, [id]: 'error' }));
    }
  };

  const buckets = useMemo(() => {
    const grouped = { upcoming: [], active: [], completed: [], cancelled: [] };
    for (const booking of bookings) grouped[bucketOf(booking)].push(booking);
    return grouped;
  }, [bookings]);

  // Story 14 - "upcoming and past scheduled deliveries", separate from the Upcoming/Active/
  // Completed booking-lifecycle tabs above (a booking can be "active" in that sense while its
  // delivery is still ahead of or behind the reference date used here). Reference date is the
  // scheduled pickup/delivery once assigned, else the booking's own startDate - same convention
  // as the backend's GET .../bookings/deliveries (Story 14).
  const deliveryBuckets = useMemo(() => {
    const now = Date.now();
    const grouped = { upcoming: [], past: [] };
    for (const booking of bookings) {
      if (booking.deliveryStatus === 'NOT_REQUIRED' || booking.status === 'cancelled') continue;
      const ref = new Date(booking.deliveryAssignment?.scheduledAt || booking.startDate).getTime();
      grouped[ref >= now ? 'upcoming' : 'past'].push(booking);
    }
    grouped.upcoming.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    grouped.past.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
    return grouped;
  }, [bookings]);

  // Client-side paging per bucket (bucketing above is done in the browser).
  const upcomingPaged = usePagedArray(buckets.upcoming, { resetKey: tab });
  const activePaged = usePagedArray(buckets.active, { resetKey: tab });
  const completedPaged = usePagedArray(buckets.completed, { resetKey: tab });
  const cancelledPaged = usePagedArray(buckets.cancelled, { resetKey: tab });
  const delivUpcomingPaged = usePagedArray(deliveryBuckets.upcoming, { resetKey: tab });
  const delivPastPaged = usePagedArray(deliveryBuckets.past, { resetKey: tab });
  if (loading) return <Spinner animation="border" role="status" />;
  if (error) return <Alert variant="danger">{error}</Alert>;

  const renderList = (paged) => {
    if (paged.total === 0) {
      return (
        <Alert variant="light">
          Nothing here yet. <Link to="/">Browse jewellery</Link>.
        </Alert>
      );
    }
    return (
      <>
        <div className="row g-3">
        {paged.rows.map((booking) => {
          const item = itemsById[booking.itemId];
          const banner = actionBanner(booking);
          return (
            <div className="col-md-6 col-lg-4" key={booking._id}>
              <Card className="h-100">
                {item?.images?.[0] && (
                  <Card.Img variant="top" src={imageUrl(item.images[0])} style={{ height: 160, objectFit: 'cover' }} />
                )}
                <Card.Body>
                  {banner && <div className="small text-primary fw-semibold mb-2">{banner}</div>}
                  <Card.Title as="h6">{item?.name || 'Jewellery item'}</Card.Title>
                  <div className="mb-1">
                    <Button
                      variant="link" size="sm"
                      className="p-0 align-baseline text-muted small font-monospace"
                      onClick={() => setOrderDetailsBooking(booking)}
                    >
                      Booking #{booking._id.slice(-6).toUpperCase()}
                    </Button>
                  </div>
                  <div className="small mb-2">
                    {new Date(booking.startDate).toLocaleDateString()} &ndash; {new Date(booking.endDate).toLocaleDateString()}
                  </div>
                  {booking.rentalAmount > 0 && (
                    <div className="small mb-2">
                      ₹{booking.rentalAmount.toLocaleString('en-IN')} rental
                      {booking.depositAmount > 0 && <> &middot; ₹{booking.depositAmount.toLocaleString('en-IN')} deposit</>}
                    </div>
                  )}
                  <Badge bg={STATUS_VARIANT[booking.status] || 'secondary'} className="mb-2">
                    {booking.status}
                  </Badge>
                  {/* tax_and_gst_calculation.md - how and when the customer paid. */}
                  {booking.paymentSummary && (
                    <div className="small text-muted mb-2">
                      {booking.paymentSummary.received
                        ? `Paid${booking.paymentSummary.method ? ` via ${booking.paymentSummary.method.replace(/_/g, ' ')}` : ''}${booking.paymentSummary.receivedAt ? ` on ${new Date(booking.paymentSummary.receivedAt).toLocaleDateString()}` : ''}`
                        : booking.paymentSummary.label}
                    </div>
                  )}
                  {booking.deliveryAssignment?.assignedTo && (
                    // Story 14 - who's delivering, and when.
                    <div className="small text-muted mb-2">
                      Delivery: {booking.deliveryAssignment.assignedTo.name}
                      {booking.deliveryAssignment.scheduledAt && ` on ${new Date(booking.deliveryAssignment.scheduledAt).toLocaleString()}`}
                    </div>
                  )}
                  <div className="d-flex gap-2 mt-2 flex-wrap">
                    <Button size="sm" as={Link} to={`/my-bookings/${booking._id}`}>
                      View Booking
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => setProductItemId(booking.itemId)}>
                      View product
                    </Button>
                    {booking.status === 'pending' &&
                      (reminded[booking._id] === 'sent' ? (
                        <small className="text-success align-self-center">Reminder sent</small>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={reminded[booking._id] === 'sending'}
                          onClick={() => handleRemind(booking._id)}
                        >
                          Remind owner
                        </Button>
                      ))}
                    {booking.status === 'confirmed' && RETURN_DUE_STATUSES.includes(booking.returnStatus) &&
                      (reminded[booking._id] === 'sent' ? (
                        <small className="text-success align-self-center">Reminder sent</small>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={reminded[booking._id] === 'sending'}
                          onClick={() => handleRemind(booking._id)}
                        >
                          Remind about return
                        </Button>
                      ))}
                  </div>
                </Card.Body>
              </Card>
            </div>
          );
        })}
        </div>
        <Pager {...paged} />
      </>
    );
  };

  return (
    <div>
      <h3 className="mb-3">My bookings</h3>
      {bookings.length === 0 ? (
        <Alert variant="light">
          You haven't booked anything yet. <Link to="/">Browse jewellery</Link>.
        </Alert>
      ) : (
        <DashboardTabs
          activeKey={tab} onSelect={setTab}
          primary={[
            ['upcoming', `Upcoming (${buckets.upcoming.length})`],
            ['active', `Active (${buckets.active.length})`],
            ['completed', `Completed (${buckets.completed.length})`],
            ['deliveries', 'Deliveries']
          ]}
          more={[
            ['cancelled', `Cancelled (${buckets.cancelled.length})`]
          ]}
        >
          <Tab.Pane eventKey="upcoming">
            {renderList(upcomingPaged)}
          </Tab.Pane>
          <Tab.Pane eventKey="active">
            {renderList(activePaged)}
          </Tab.Pane>
          <Tab.Pane eventKey="completed">
            {renderList(completedPaged)}
          </Tab.Pane>
          {/* Cancelled bookings that no role still needs to act on - kept out of the lists above. */}
          <Tab.Pane eventKey="cancelled">
            {renderList(cancelledPaged)}
          </Tab.Pane>
          {/* Story 14 - upcoming vs past scheduled deliveries, keyed off the delivery
              schedule/startDate rather than the booking lifecycle above. */}
          <Tab.Pane eventKey="deliveries">
            <h6 className="text-uppercase text-muted small mb-2">Upcoming ({deliveryBuckets.upcoming.length})</h6>
            {renderList(delivUpcomingPaged)}
            <h6 className="text-uppercase text-muted small mb-2 mt-4">Past ({deliveryBuckets.past.length})</h6>
            {renderList(delivPastPaged)}
          </Tab.Pane>
        </DashboardTabs>
      )}

      <ProductDetailsModal itemId={productItemId} show={!!productItemId} onHide={() => setProductItemId(null)} />
      <OrderDetailsModal
        booking={orderDetailsBooking}
        itemName={orderDetailsBooking ? (itemsById[orderDetailsBooking.itemId]?.name || '') : ''}
        show={!!orderDetailsBooking}
        onHide={() => setOrderDetailsBooking(null)}
        onViewProduct={orderDetailsBooking ? () => setProductItemId(orderDetailsBooking.itemId) : undefined}
      />
    </div>
  );
}
